import { useEffect, useRef, useState, useCallback } from 'react';
import { useMqttStore } from '../stores/mqttStore';
import * as mqttService from '../services/mqttService';

type UseMqttSubscribeOptions = {
  qos?: 0 | 1 | 2;
  enabled?: boolean;
};

const useMqttSubscribe = <T = unknown>(
  topic: string,
  options?: UseMqttSubscribeOptions
) => {
  const { qos = 0, enabled = true } = options ?? {};
  const isConnected = useMqttStore(state => state.isConnected);
  const [lastMessage, setLastMessage] = useState<T | null>(null);
  const [lastTopic, setLastTopic] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const handlerRef = useRef<((topic: string, payload: string) => void) | null>(
    null
  );

  const handler = useCallback((receivedTopic: string, payload: string) => {
    try {
      const parsed = JSON.parse(payload) as T;
      setLastMessage(parsed);
      setLastTopic(receivedTopic);
      setError(null);
    } catch {
      setError('Failed to parse MQTT message');
    }
  }, []);

  useEffect(() => {
    if (!enabled || !isConnected || !topic) return;

    handlerRef.current = handler;
    mqttService.subscribe(topic, handler, qos);

    return () => {
      if (handlerRef.current) {
        mqttService.unsubscribe(topic, handlerRef.current);
        handlerRef.current = null;
      }
    };
  }, [topic, qos, enabled, isConnected, handler]);

  return {
    lastMessage,
    lastTopic,
    error,
    isSubscribed: isConnected && enabled
  };
};

export default useMqttSubscribe;
